import { ProfileT, SimilarProfileT } from "shared/types/user";

import { VIEW_FROM_SEARCH } from "shared/constants";
import {
  DISLIKE_USER_PROFILE_URL,
  FEED_SIMILAR_PROFILES,
  GET_COUNTRY,
  LIKE_USER_PROFILE,
  MY_PROFILE_PREFERENCES_AGE,
  MY_PROFILE_URL,
  PROFILE_VIEW_URL,
  REMOVE_FAVOR_USER_PROFILE,
  SEND_QUICK_MESSAGE,
  SET_FAVOR_USER_PROFILE,
  UNBLOCK_USER_URL,
  USER_ACTION_URL,
  USER_AUTOR_REPLY_VIEW,
  USER_BLOCK_URL,
  USER_MEDIA,
  USER_REPORT_URL,
  USER_VIEW_URL,
  VIEW_EVENT,
  WINK_USER_PROFILE,
} from "shared/constants/apiUrls";

import { get, post } from "./axios";

/**
 * Get self or viewed profile data
 */
export const getProfile = (
  uid: number | null = null,
  customHeader: Record<string, unknown> | null = null
) => {
  return uid
    ? getViewedProfile(uid, customHeader)
    : getSelfProfile(customHeader);
};

/**
 * Get self profile data
 */
const getSelfProfile = (
  customHeader: Record<string, unknown> | null = null
) => {
  return get<ProfileT>(
    `${
      import.meta.env.VITE_APP_API_HOST
    }${MY_PROFILE_URL}?ms=${new Date().getTime()}`,
    customHeader ? { headers: customHeader } : null
  );
};

/**
 * Get profile data of user that we view
 */
const getViewedProfile = (
  uid: number,
  customHeader: Record<string, unknown> | null = null
) => {
  return get<ProfileT>(
    import.meta.env.VITE_APP_API_HOST +
      PROFILE_VIEW_URL.replace("{uid}", uid.toString()),
    customHeader ? { headers: customHeader } : null
  );
};

/**
 * Get user media files by user id
 */
export const getUserMedia = (userUid: number) => {
  return get(
    import.meta.env.VITE_APP_API_HOST +
      USER_MEDIA.replace("{uid}", userUid.toString())
  );
};

/**
 * Set user like favored
 */
export const favorUser = (params: { uid: number; source?: string | null }) => {
  return post(
    import.meta.env.VITE_APP_API_HOST + SET_FAVOR_USER_PROFILE,
    params
  );
};

/**
 * Remove user like favored
 */
export const removeFavorUser = (params: { uid: number }) => {
  return post(
    import.meta.env.VITE_APP_API_HOST + REMOVE_FAVOR_USER_PROFILE,
    params
  );
};

/**
 * Update user preference age
 */
export const updatePreferencesAge = (attributes: {
  age_from: number;
  age_to: number;
}) => {
  return post(
    import.meta.env.VITE_APP_API_HOST + MY_PROFILE_PREFERENCES_AGE,
    attributes
  );
};

/**
 * Block user
 */
export const blockUser = (params: { uid: number }) => {
  return post(import.meta.env.VITE_APP_API_HOST + USER_BLOCK_URL, params);
};

/**
 * Unblock user
 */
export const unblockUser = (params: { uid: number }) => {
  return post(import.meta.env.VITE_APP_API_HOST + UNBLOCK_USER_URL, params);
};

/**
 * Report abuse
 */
export const reportUser = (params: {
  report_to: number;
  reason: string;
  description: string;
}) => {
  return post(import.meta.env.VITE_APP_API_HOST + USER_REPORT_URL, params);
};

/**
 * Get user data
 */
export const viewUser = (uid: number) => {
  return get(
    import.meta.env.VITE_APP_API_HOST +
      USER_VIEW_URL.replace(/{uid}/g, uid.toString())
  );
};

/**
 * Like user profile
 */
export const likeUser = (params: { uid: number; source?: string }) => {
  return post(import.meta.env.VITE_APP_API_HOST + LIKE_USER_PROFILE, params);
};

/**
 * Dislike user profile
 */
export const dislikeUser = (params: { uid: number }) => {
  return post(
    import.meta.env.VITE_APP_API_HOST + DISLIKE_USER_PROFILE_URL,
    params
  );
};

/**
 * Get user actions status
 */
export const getActionsStatuses = (uid: number) => {
  return get(
    import.meta.env.VITE_APP_API_HOST +
      USER_ACTION_URL.replace(/{uid}/g, uid.toString())
  );
};

/**
 * Send wink to user
 */
export const winkUser = (uid: number, source: string, key = "") => {
  return post(import.meta.env.VITE_APP_API_HOST + WINK_USER_PROFILE, {
    uid,
    source,
    key,
  });
};

/**
 * Send a quick message to a user.
 */
export const quickMessage = (uid: number, message: string, key = "") => {
  return post(import.meta.env.VITE_APP_API_HOST + SEND_QUICK_MESSAGE, {
    uid,
    message,
    key,
  });
};

/**
 * Send information about view user profile
 */
export const trackUserView = (uid: number, isFromSearch: boolean) => {
  return post(`${import.meta.env.VITE_APP_API_HOST}${VIEW_EVENT}`, {
    uid,
    event: isFromSearch ? VIEW_FROM_SEARCH : "",
  });
};

/**
 * Send data for start autoreply scenario
 */
export const sendAutoReply = (uid: number) => {
  return post(`${import.meta.env.VITE_APP_API_HOST}${USER_AUTOR_REPLY_VIEW}`, {
    uid,
  });
};

/**
 * Get similar profiles
 */
export const getSimilarProfiles = (uid: number) => {
  return get<SimilarProfileT[]>(
    `${
      import.meta.env.VITE_APP_API_HOST
    }${FEED_SIMILAR_PROFILES}${uid.toString()}`
  );
};

/**
 * Get current user country
 * @returns {Promise<AxiosResponse<*>>}
 */
export const getCurrentCountry = () => {
  return get(`${import.meta.env.VITE_APP_API_HOST}${GET_COUNTRY}`);
};
